/* GCS Aktionen (Startseite) – nur von sections/gcs-campaigns.liquid geladen (defer).
   <gcs-campaigns>: Video-Fächer mit Endlosschleife. Position `pos` ist ein Kommawert (Kartenindex), jede Karte bekommt
   aus ihrem Abstand zur Mitte Versatz, Drehung, Größe, Abdunkelung und Deckkraft (nur transform/opacity).
   Werte kommen aus CSS-Variablen (--cp-x1 … auf .gcs-cp), damit CSS-Startpositionen und JS identisch sind.
   Ziehen: Pointer Events am Viewport, Richtungssperre (waagrecht = Fächer, senkrecht = Seite scrollt, touch-action: pan-y),
   Klick vs. Ziehen ab 8 px, Loslassen: Distanz + Geschwindigkeit, höchstens 3 Karten, dann rAF-Feder (Halbimplizit-Euler).
   Video: nur die aktive Karte spielt eine stumme Vorschau (erste 15 s in Schleife, Quelle wird erst dann gesetzt), sobald die Section zu 35 % sichtbar
   ist und die Feder steht. play()-Promise abgefangen: blockiert = Standbild bleibt. Verlassene Karten pausieren und geben
   ihre Quelle nach dem Ausblenden frei. Play öffnet <dialog> mit Ton und Steuerelementen (Vorschau pausiert).
   Bewegung reduzieren: keine Vorschau, keine Feder (sofortiger Wechsel), Ziehen wechselt erst beim Loslassen. */
(() => {
  if (customElements.get('gcs-campaigns')) return;

  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reduced = () => motionQuery.matches;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const mod = (a, n) => ((a % n) + n) % n;
  const saveData = () => {
    const c = navigator.connection;
    return !!(c && c.saveData);
  };

  const TAP_SLOP = 8; // px: darunter ist es ein Tippen, darüber Ziehen
  const MAX_SKIP = 3; // höchstens so viele Karten pro Wisch
  const FLICK_PX = 250; // px/s beim Loslassen: ab hier zählt der Wisch als Schwung (mindestens eine Karte weiter)
  const EXTRA_FROM = 1500; // px/s: ab hier eine Karte zusätzlich …
  const EXTRA_STEP = 1000; // … und je weitere 1000 px/s noch eine (gedeckelt über MAX_SKIP)
  const STIFFNESS = 210;
  const DAMPING = 2 * 0.86 * Math.sqrt(STIFFNESS);
  const RELEASE_SRC_MS = 450;
  const PREVIEW_MAX_S = 15; // stumme Vorschau spielt nur die ersten 15 s in Schleife (Reels sind bis 3 min lang)
  const DEFAULTS = {
    x1: 0.64,
    x2: 1.12,
    y1: 0.055,
    y2: 0.13,
    r1: 10,
    r2: 16,
    s1: 0.88,
    s2: 0.76,
    d1: 0.58,
    d2: 0.75,
    depth: 1,
    bn: 0,
  };

  class GcsCampaigns extends HTMLElement {
    connectedCallback() {
      if (this.booted) return;
      this.booted = true;
      if (!('IntersectionObserver' in window)) {
        this.init();
        return;
      }
      // Erst initialisieren, wenn die Section nahe am Bildschirm ist (vorher steht der CSS-Fächer)
      this.nearObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.nearObserver.disconnect();
          this.nearObserver = null;
          this.init();
        },
        { rootMargin: '600px 0px 600px 0px' },
      );
      this.nearObserver.observe(this);
    }

    disconnectedCallback() {
      if (this.nearObserver) this.nearObserver.disconnect();
      if (this.viewObserver) this.viewObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.renderRaf) cancelAnimationFrame(this.renderRaf);
      this.raf = 0;
      this.renderRaf = 0;
      clearTimeout(this.previewTimer);
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.onMotion && motionQuery.removeEventListener) motionQuery.removeEventListener('change', this.onMotion);
      if (this.dialogOpen) {
        this.stopDialogVideo();
        this.unlock();
        this.dialogOpen = false;
      }
      if (this.items) this.items.forEach((item) => this.unloadPreview(item));
    }

    init() {
      if (this.ready) return;
      this.viewport = this.querySelector('[data-cp-viewport]');
      this.cards = [...this.querySelectorAll('[data-cp-card]')];
      this.n = this.cards.length;
      if (!this.viewport || !this.n) return;
      this.ready = true;

      const text = (el) => (el ? el.textContent.trim() : '');
      this.items = this.cards.map((card, i) => {
        const hit = card.querySelector('[data-cp-play]');
        return {
          card,
          i,
          shade: card.querySelector('.gcs-cp__shade'),
          text: card.querySelector('[data-cp-text]'),
          hit,
          play: card.querySelector('.gcs-cp__play'),
          badgeEl: card.querySelector('[data-cp-badge]'),
          video: card.querySelector('[data-cp-video]'),
          previewSrc: card.dataset.previewSrc || '',
          // Volle Quelle = Ziel des Play-Links (ohne JS öffnet er die MP4-Datei direkt)
          fullSrc: hit ? hit.getAttribute('href') || '' : '',
          poster: card.dataset.poster || '',
          title: text(card.querySelector('[data-cp-title]')),
          subline: text(card.querySelector('[data-cp-subline]')),
          badge: text(card.querySelector('[data-cp-badge]')),
          last: {},
          unloadTimer: 0,
        };
      });

      this.status = this.querySelector('[data-cp-status]');
      this.counter = this.querySelector('[data-cp-current]');
      this.dialog = this.querySelector('[data-cp-dialog]');
      if (this.dialog) {
        this.dlgVideo = this.dialog.querySelector('[data-cp-dialog-video]');
        this.dlgTitle = this.dialog.querySelector('[data-cp-dialog-title]');
        this.dlgSub = this.dialog.querySelector('[data-cp-dialog-sub]');
        this.dlgBadge = this.dialog.querySelector('[data-cp-dialog-badge]');
        this.dlgCount = this.dialog.querySelector('[data-cp-dialog-count]');
      }

      this.pos = 0;
      this.target = 0;
      this.vel = 0;
      this.active = 0;
      this.announced = 0;
      this.inView = false;
      this.previewOn = this.hasAttribute('data-preview');

      this.step = this.step.bind(this);
      this.renderNow = this.renderNow.bind(this);

      this.readParams();
      this.render();
      // Ab jetzt steuert JS alle Werte inline, die CSS-Startpositionen werden nicht mehr gebraucht
      this.cards.forEach((card) => card.removeAttribute('data-cp-pos'));
      this.classList.add('is-ready');

      this.bind();
    }

    bind() {
      const vp = this.viewport;
      vp.addEventListener('pointerdown', (e) => this.onDown(e));
      vp.addEventListener('pointermove', (e) => this.onMove(e), { passive: true });
      vp.addEventListener('pointerup', (e) => this.onUp(e, false));
      vp.addEventListener('pointercancel', (e) => this.onUp(e, true));
      vp.addEventListener('dragstart', (e) => e.preventDefault());
      this.addEventListener('click', (e) => this.onClickCapture(e), true);
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('keydown', (e) => this.onKey(e));
      this.addEventListener('shopify:block:select', (e) => {
        const card = e.target.closest ? e.target.closest('[data-cp-card]') : null;
        const i = this.cards.indexOf(card);
        if (i >= 0) this.goTo(this.target + this.delta(i), { instant: true });
      });

      if (this.dialog) {
        this.dialog.addEventListener('click', (e) => this.onDialogClick(e));
        this.dialog.addEventListener('close', () => this.onDialogClose());
      }

      this.items.forEach((item) => {
        if (!item.video) return;
        item.video.addEventListener('playing', () => {
          if (item.i === this.active && this.canPreview()) item.card.classList.add('is-playing');
          else this.stopPreview(item);
        });
        item.video.addEventListener('timeupdate', () => {
          const v = item.video;
          if (!v.paused && v.currentTime >= PREVIEW_MAX_S) v.currentTime = 0;
        });
        item.video.addEventListener('error', () => {
          item.card.classList.remove('is-playing');
          // Abgeleitete 720p-Quelle nicht da: einmalig auf die 1080p-Quelle aus Liquid wechseln
          if (item.fullSrc && item.previewSrc !== item.fullSrc && item.video.getAttribute('src') === item.previewSrc) {
            item.previewSrc = item.fullSrc;
            if (item.i === this.active && this.canPreview()) this.startPreview(item);
          }
        });
      });

      this.viewObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.35;
          this.syncPreview();
        },
        { threshold: [0, 0.35, 0.6] },
      );
      this.viewObserver.observe(vp);

      if ('ResizeObserver' in window) {
        let width = vp.clientWidth;
        this.resizeObserver = new ResizeObserver(() => {
          if (vp.clientWidth === width) return;
          width = vp.clientWidth;
          this.readParams();
          this.render();
        });
        this.resizeObserver.observe(vp);
      }

      this.onVisibility = () => this.syncPreview();
      document.addEventListener('visibilitychange', this.onVisibility);
      this.onMotion = () => {
        if (reduced() && this.raf) {
          cancelAnimationFrame(this.raf);
          this.raf = 0;
          this.pos = this.target;
          this.vel = 0;
          this.render();
        }
        this.syncPreview();
      };
      if (motionQuery.addEventListener) motionQuery.addEventListener('change', this.onMotion);
    }

    /* ---------- Geometrie ---------- */

    readParams() {
      const cs = getComputedStyle(this);
      const p = {};
      Object.keys(DEFAULTS).forEach((key) => {
        const v = parseFloat(cs.getPropertyValue(`--cp-${key}`));
        p[key] = Number.isFinite(v) ? v : DEFAULTS[key];
      });
      // Tiefe begrenzen, damit der Sprung am Schleifenende immer unsichtbar passiert
      p.depth = clamp(Math.round(p.depth), 0, Math.max(0, Math.floor((this.n - 1) / 2)));
      // Ausblenden bis spätestens 0,1 vor der Schleifenkante (n / 2): dort springt eine Karte die Seite, dann ist sie unsichtbar
      p.fadeEnd = Math.min(p.depth + 0.5, this.n / 2 - 0.1);
      this.p = p;
      this.cardWidth = this.cards[0].offsetWidth || 1;
    }

    /* Pixel, um die sich der Fächer pro Karte verschiebt (Abstand Mitte → erster Nachbar) */
    stepPx() {
      return Math.max(40, this.p.x1 * this.cardWidth);
    }

    /* Stückweise linear: 0 → v1 (Stufe 1) → v2 (Stufe 2) → weiter mit derselben Steigung */
    level(a, v1, v2) {
      return a <= 1 ? v1 * a : v1 + (v2 - v1) * (a - 1);
    }

    requestRender() {
      if (this.renderRaf) return;
      this.renderRaf = requestAnimationFrame(this.renderNow);
    }

    renderNow() {
      this.renderRaf = 0;
      this.render();
    }

    render() {
      const { p, n } = this;
      const half = n / 2;
      this.items.forEach((item) => {
        const d = mod(item.i - this.pos + half, n) - half;
        const a = Math.abs(d);
        const sign = d < 0 ? -1 : 1;
        const x = sign * this.level(a, p.x1, p.x2) * 100;
        const y = this.level(a, p.y1, p.y2) * 100;
        const r = sign * this.level(a, p.r1, p.r2);
        const s = Math.max(0.3, 1 - this.level(a, 1 - p.s1, 1 - p.s2));
        const opacity = clamp((p.fadeEnd - a) / Math.max(0.1, p.fadeEnd - p.depth), 0, 1);
        this.apply(item, {
          transform: `translate3d(${x.toFixed(3)}%, ${y.toFixed(3)}%, 0) rotate(${r.toFixed(3)}deg) scale(${s.toFixed(4)})`,
          opacity: opacity.toFixed(3),
          visibility: opacity > 0.001 ? 'visible' : 'hidden',
          zIndex: String(100 - Math.round(a * 10)),
          shade: clamp(this.level(a, p.d1, p.d2), 0, 0.9).toFixed(3),
          front: clamp(1 - a * 2.5, 0, 1).toFixed(3),
          badge: (p.bn >= 1 ? 1 : clamp(1 - a * 2.5, 0, 1)).toFixed(3),
        });
      });
    }

    apply(item, v) {
      const last = item.last;
      const cs = item.card.style;
      if (last.transform !== v.transform) cs.transform = last.transform = v.transform;
      if (last.opacity !== v.opacity) cs.opacity = last.opacity = v.opacity;
      if (last.visibility !== v.visibility) cs.visibility = last.visibility = v.visibility;
      if (last.zIndex !== v.zIndex) cs.zIndex = last.zIndex = v.zIndex;
      if (last.shade !== v.shade) {
        last.shade = v.shade;
        if (item.shade) item.shade.style.opacity = v.shade;
      }
      if (last.front !== v.front) {
        last.front = v.front;
        if (item.text) item.text.style.opacity = v.front;
        if (item.play) item.play.style.opacity = v.front;
      }
      if (last.badge !== v.badge) {
        last.badge = v.badge;
        if (item.badgeEl) item.badgeEl.style.opacity = v.badge;
      }
    }

    /* ---------- Navigation ---------- */

    /* kürzester Weg (mit Schleife) von der Zielkarte zu Karte i */
    delta(i) {
      let d = mod(i - this.target, this.n);
      if (d > this.n / 2) d -= this.n;
      return d;
    }

    move(dir) {
      if (this.n < 2) return;
      this.goTo(this.target + dir);
    }

    goTo(t, opts = {}) {
      this.target = t;
      this.updateActive();
      if (opts.instant || reduced()) {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = 0;
        this.pos = t;
        this.vel = 0;
        this.render();
        this.settle();
        return;
      }
      this.startSpring();
    }

    startSpring() {
      clearTimeout(this.previewTimer);
      if (this.raf) return;
      this.lastT = performance.now();
      this.raf = requestAnimationFrame(this.step);
    }

    step(now) {
      const dt = clamp((now - this.lastT) / 1000, 0, 0.05);
      this.lastT = now;
      const steps = Math.max(1, Math.ceil(dt / 0.004));
      const h = dt / steps;
      for (let k = 0; k < steps; k += 1) {
        const acc = STIFFNESS * (this.target - this.pos) - DAMPING * this.vel;
        this.vel += acc * h;
        this.pos += this.vel * h;
      }
      if (Math.abs(this.target - this.pos) < 0.0015 && Math.abs(this.vel) < 0.02) {
        this.pos = this.target;
        this.vel = 0;
        this.raf = 0;
        this.render();
        this.settle();
        return;
      }
      this.render();
      this.raf = requestAnimationFrame(this.step);
    }

    settle() {
      // Zahlen klein halten (Schleife): pos und target gemeinsam um Vielfache von n verschieben
      const shift = Math.round(this.target / this.n) * this.n;
      if (shift) {
        this.target -= shift;
        this.pos -= shift;
      }
      this.updateActive();
      if (this.active !== this.announced) {
        this.announced = this.active;
        this.announce();
      }
      this.schedulePreview();
    }

    updateActive() {
      const idx = mod(Math.round(this.target), this.n);
      if (idx === this.active && this.activeSynced) return;
      const prev = this.items[this.active];
      const hadFocus = prev && prev.hit && document.activeElement === prev.hit;
      this.active = idx;
      this.activeSynced = true;
      this.items.forEach((item) => {
        const on = item.i === idx;
        item.card.classList.toggle('is-active', on);
        if (on) item.card.removeAttribute('aria-hidden');
        else item.card.setAttribute('aria-hidden', 'true');
        if (item.hit) {
          if (on) item.hit.removeAttribute('tabindex');
          else item.hit.setAttribute('tabindex', '-1');
        }
        if (!on) this.stopPreview(item);
      });
      if (this.counter) this.counter.textContent = String(idx + 1);
      if (hadFocus && this.items[idx].hit) this.items[idx].hit.focus({ preventScroll: true });
    }

    announce() {
      if (!this.status) return;
      const item = this.items[this.active];
      this.status.textContent = `Aktion ${item.i + 1} von ${this.n}${item.title ? `: ${item.title}` : ''}`;
    }

    /* ---------- Ziehen ---------- */

    onDown(e) {
      if (this.n < 2 || this.drag || !e.isPrimary) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      // Maus: keine Textauswahl und kein natives Ziehen von Bild/Link (click kommt trotzdem)
      if (e.pointerType === 'mouse') e.preventDefault();
      this.drag = {
        id: e.pointerId,
        x0: e.clientX,
        y0: e.clientY,
        locked: false,
        startPos: this.pos,
        samples: [{ x: e.clientX, t: e.timeStamp }],
      };
    }

    onMove(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      if (!d.locked) {
        const dx = e.clientX - d.x0;
        const dy = e.clientY - d.y0;
        if (Math.abs(dx) < TAP_SLOP && Math.abs(dy) < TAP_SLOP) return;
        if (Math.abs(dy) >= Math.abs(dx)) {
          // senkrecht: die Seite scrollt (touch-action: pan-y), der Fächer bleibt stehen
          this.drag = null;
          return;
        }
        d.locked = true;
        // ab hier 1:1 mit dem Finger; die Schwelle wird abgezogen (kein Sprung), ein großer erster Schritt bleibt erhalten
        d.x0 += Math.sign(dx) * TAP_SLOP;
        if (this.raf) {
          cancelAnimationFrame(this.raf);
          this.raf = 0;
        }
        this.vel = 0;
        d.startPos = this.pos;
        this.dragging = true;
        this.suppressUntil = Infinity;
        try {
          this.viewport.setPointerCapture(d.id);
        } catch (err) {
          /* Pointer bereits weg */
        }
        this.classList.add('is-dragging');
        clearTimeout(this.previewTimer);
        this.syncPreview();
      }
      d.samples.push({ x: e.clientX, t: e.timeStamp });
      if (d.samples.length > 12) d.samples.shift();
      if (reduced()) return; // kein Mitziehen, Wechsel erst beim Loslassen
      this.pos = d.startPos - (e.clientX - d.x0) / this.stepPx();
      this.requestRender();
    }

    onUp(e, cancelled) {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      this.drag = null;
      if (!d.locked) return;
      this.dragging = false;
      this.classList.remove('is-dragging');
      // Klick direkt nach dem Ziehen unterdrücken (Maus feuert click nach mouseup)
      this.suppressUntil = performance.now() + 60;
      try {
        if (this.viewport.hasPointerCapture(d.id)) this.viewport.releasePointerCapture(d.id);
      } catch (err) {
        /* egal */
      }

      const step = this.stepPx();
      const samples = d.samples;
      const lastS = samples[samples.length - 1];
      let firstS = samples[0];
      for (let k = 0; k < samples.length; k += 1) {
        if (lastS.t - samples[k].t <= 100) {
          firstS = samples[k];
          break;
        }
      }
      const span = (lastS.t - firstS.t) / 1000;
      // Geschwindigkeit in px/s (positiv = Finger nach rechts); stand der Finger vor dem Loslassen, zählt sie nicht
      let vPx = span > 0.008 ? (lastS.x - firstS.x) / span : 0;
      if (cancelled || e.timeStamp - lastS.t > 90) vPx = 0;
      const speed = Math.abs(vPx);
      const dir = vPx < 0 ? 1 : -1; // Finger nach links = nächste Karte

      const base = Math.round(d.startPos);
      let t;
      if (reduced()) {
        const dx = e.clientX - d.x0;
        const jump = Math.abs(dx) > 40 ? -Math.sign(dx) : speed > FLICK_PX ? dir : 0;
        this.goTo(base + jump, { instant: true });
        return;
      }
      if (speed > FLICK_PX) {
        // Schwung: mindestens bis zur nächsten Karte in Wischrichtung, schnelle Wische eine oder zwei mehr
        const extra = Math.floor(Math.max(0, speed - EXTRA_FROM) / EXTRA_STEP) + (speed > EXTRA_FROM ? 1 : 0);
        t = dir > 0 ? Math.floor(this.pos + 0.001) + 1 + extra : Math.ceil(this.pos - 0.001) - 1 - extra;
      } else {
        t = Math.round(this.pos); // langsam losgelassen: nächstliegende Karte
      }
      t = clamp(t, base - MAX_SKIP, base + MAX_SKIP);
      this.vel = clamp(-vPx / step, -8, 8); // Feder startet mit der Fingergeschwindigkeit (Karten/s)
      this.goTo(t);
    }

    onClickCapture(e) {
      if (this.dialog && this.dialog.contains(e.target)) return;
      if (performance.now() < (this.suppressUntil || 0)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    onClick(e) {
      if (this.dialog && this.dialog.contains(e.target)) return;
      const card = e.target.closest('[data-cp-card]');
      if (card) {
        const item = this.items[this.cards.indexOf(card)];
        if (!item) return;
        if (item.i !== this.active) {
          // Tap auf eine Nachbarkarte holt sie in die Mitte
          e.preventDefault();
          this.goTo(this.target + this.delta(item.i));
          return;
        }
        const hit = e.target.closest('[data-cp-play]');
        if (hit && this.canDialog(item)) {
          e.preventDefault();
          this.openDialog(item, hit);
        }
        return;
      }
      if (e.target.closest('[data-cp-prev]')) this.move(-1);
      else if (e.target.closest('[data-cp-next]')) this.move(1);
    }

    onKey(e) {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const dir = e.key === 'ArrowLeft' ? -1 : 1;
      if (this.dialogOpen) {
        if (e.target.closest('video')) return; // Pfeiltasten spulen im Video
        e.preventDefault();
        this.dialogStep(dir);
        return;
      }
      if (e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      e.preventDefault();
      this.move(dir);
    }

    /* ---------- Vorschau-Video ---------- */

    canPreview() {
      return (
        this.previewOn &&
        this.inView &&
        !this.dialogOpen &&
        !this.dragging &&
        !this.raf &&
        !reduced() &&
        !saveData() &&
        document.visibilityState !== 'hidden'
      );
    }

    schedulePreview() {
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => this.syncPreview(), 160);
    }

    syncPreview() {
      if (!this.items) return;
      const want = this.canPreview() ? this.items[this.active] : null;
      this.items.forEach((item) => {
        if (item !== want) this.stopPreview(item);
      });
      if (want) this.startPreview(want);
    }

    startPreview(item) {
      const v = item.video;
      if (!v || !item.previewSrc) return;
      clearTimeout(item.unloadTimer);
      if (!v.paused && item.card.classList.contains('is-playing')) return;
      if (v.getAttribute('src') !== item.previewSrc) {
        v.src = item.previewSrc;
      } else if (v.currentTime > 0 && !item.card.classList.contains('is-playing')) {
        try {
          v.currentTime = 0;
        } catch (err) {
          /* noch keine Metadaten */
        }
      }
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      let promise;
      try {
        promise = v.play();
      } catch (err) {
        promise = null;
      }
      // Blockiert (Stromsparmodus, Datensparen): Standbild mit Play-Button bleibt, nie ein schwarzes Rechteck
      if (promise && promise.catch) promise.catch(() => item.card.classList.remove('is-playing'));
    }

    stopPreview(item) {
      const v = item.video;
      if (!v) return;
      item.card.classList.remove('is-playing');
      if (!v.paused) v.pause();
      if (!v.getAttribute('src')) return;
      // Nach dem Ausblenden Quelle freigeben (kein weiterer Download, kein Decoder im Hintergrund)
      clearTimeout(item.unloadTimer);
      item.unloadTimer = setTimeout(() => {
        if (item.i === this.active && this.canPreview()) return;
        this.unloadPreview(item);
      }, RELEASE_SRC_MS);
    }

    unloadPreview(item) {
      const v = item.video;
      if (!v) return;
      clearTimeout(item.unloadTimer);
      item.card.classList.remove('is-playing');
      if (!v.getAttribute('src')) return;
      v.pause();
      v.removeAttribute('src');
      try {
        v.load();
      } catch (err) {
        /* egal */
      }
    }

    /* ---------- Overlay ---------- */

    canDialog(item) {
      return !!(this.dialog && this.dlgVideo && item.fullSrc && typeof this.dialog.showModal === 'function');
    }

    openDialog(item, opener) {
      this.opener = opener;
      this.dialogOpen = true;
      this.syncPreview(); // Vorschau pausieren: höchstens ein Video dekodiert
      this.fillDialog(item);
      try {
        this.dialog.showModal();
      } catch (err) {
        this.dialogOpen = false;
        window.location.href = item.fullSrc;
        return;
      }
      this.lock();
      this.playDialog(); // noch in der Nutzer-Geste: startet mit Ton
    }

    fillDialog(item) {
      this.dialogIndex = item.i;
      if (this.dlgTitle) this.dlgTitle.textContent = item.title || 'Aktion';
      if (this.dlgSub) {
        this.dlgSub.textContent = item.subline;
        this.dlgSub.hidden = !item.subline;
      }
      if (this.dlgBadge) {
        this.dlgBadge.textContent = item.badge;
        this.dlgBadge.hidden = !item.badge;
      }
      if (this.dlgCount) this.dlgCount.textContent = `${item.i + 1} / ${this.n}`;
      const v = this.dlgVideo;
      v.pause();
      if (item.poster) v.poster = item.poster;
      else v.removeAttribute('poster');
      v.src = item.fullSrc;
      v.muted = false;
      v.setAttribute('aria-label', item.title ? `Video: ${item.title}` : 'Video');
    }

    playDialog() {
      let promise;
      try {
        promise = this.dlgVideo.play();
      } catch (err) {
        promise = null;
      }
      // Wird der Start verweigert, bleiben die nativen Steuerelemente zum Starten
      if (promise && promise.catch) promise.catch(() => {});
    }

    dialogStep(dir) {
      if (this.n < 2) return;
      let i = this.dialogIndex;
      for (let k = 0; k < this.n; k += 1) {
        i = mod(i + dir, this.n);
        if (this.items[i].fullSrc) break;
      }
      // Fächer hinter dem Overlay ohne Animation mitziehen, damit beim Schließen dieselbe Aktion in der Mitte steht
      this.goTo(this.target + this.delta(i), { instant: true });
      this.fillDialog(this.items[i]);
      this.playDialog();
    }

    onDialogClick(e) {
      if (e.target.closest('[data-cp-close]')) this.closeDialog();
      else if (e.target.closest('[data-cp-dialog-prev]')) this.dialogStep(-1);
      else if (e.target.closest('[data-cp-dialog-next]')) this.dialogStep(1);
      else if (!e.target.closest('[data-cp-keep]')) this.closeDialog(); // Tap außerhalb von Video und Text
    }

    closeDialog() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    onDialogClose() {
      this.stopDialogVideo();
      this.dialogOpen = false;
      this.unlock();
      const item = this.items[this.active];
      const back = (item && item.hit) || this.opener;
      if (back && back.focus && document.contains(back)) back.focus({ preventScroll: true });
      this.syncPreview();
    }

    stopDialogVideo() {
      const v = this.dlgVideo;
      if (!v) return;
      v.pause();
      v.removeAttribute('src');
      v.removeAttribute('poster');
      try {
        v.load();
      } catch (err) {
        /* egal */
      }
    }

    lock() {
      if (this.locked) return;
      if (window.GCS && typeof window.GCS.lockScroll === 'function') {
        window.GCS.lockScroll();
        this.locked = true;
      }
    }

    unlock() {
      if (!this.locked) return;
      this.locked = false;
      if (window.GCS && typeof window.GCS.unlockScroll === 'function') window.GCS.unlockScroll();
    }
  }

  customElements.define('gcs-campaigns', GcsCampaigns);
})();
